import { ArticleList } from "@babackapp/customer-ui/components/article";
import { LineItemRow } from "./line-item/line-item-row";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  SectionHeader,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import { Separator as UiSeparator } from "@babackapp/customer-ui/components/separator";
import {
  ReceiptLine,
  ReceiptLineLabel,
  ReceiptLineValue,
} from "@babackapp/customer-ui/components/receipt-line";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { Separator } from "@babackapp/ui/components/separator";
import { EnterIcon } from "@radix-ui/react-icons";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { useWidgetParams } from "../hooks/use-widget-params";
import { SummaryLineItem } from "./summary-line-item";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { useSummaryConfirm } from "../hooks/use-summary-confirm";
import { useSummary } from "../hooks/use-summary";
import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import { AlertCircleIcon } from "lucide-react";
import { useAuth } from "@babackapp/auth-client/hooks";

export function ReturnRequestSummary() {
  const navigate = useNavigate();
  const { orderId, shopId, logoUrl } = useWidgetParams();
  const t = useCustomText();
  const { onConfirm, isValidating } = useSummaryConfirm();
  const { isAuthenticated } = useAuth();
  const connectedUserTooltip = t("misc.connectedUserData");
  const {
    summary,
    shop,
    order,
    hasConflict,
    remainingReturnableItems,
    isRetractationItem,
    handleRemove,
    exchangeItems,
    refundItems,
    discountItems,
    orderLineItems,
  } = useSummary();

  return (
    <WidgetPage>
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>{t("summary.title")}</HeaderTitle>
        <HeaderDescription>{t("summary.description")}</HeaderDescription>
      </Header>

      <UserPortalCallout
        texts={shop?.preferences?.userPortal?.confirmationMessage}
      />

      <div className="w-full mt-4 flex flex-col gap-6">
        {exchangeItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("summary.exchangeItems")}</SectionHeader>
            <ArticleList size="sm">
              {exchangeItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={orderLineItems}
                  order={order ?? null}
                  currency={summary.currency}
                  isRetractation={isRetractationItem(item.reasonId)}
                  isConnectedUser={isAuthenticated}
                  connectedUserTooltip={connectedUserTooltip}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}
        {refundItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("summary.refundItems")}</SectionHeader>
            <ArticleList size="sm">
              {refundItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={orderLineItems}
                  order={order ?? null}
                  currency={summary.currency}
                  isRetractation={isRetractationItem(item.reasonId)}
                  isConnectedUser={isAuthenticated}
                  connectedUserTooltip={connectedUserTooltip}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}
        {discountItems.length > 0 && (
          <div className="flex flex-col gap-2">
            <SectionHeader>{t("summary.discountItems")}</SectionHeader>
            <ArticleList size="sm">
              {discountItems.map((item) => (
                <SummaryLineItem
                  key={item.lineItemId}
                  item={item}
                  orderLineItems={orderLineItems}
                  order={order ?? null}
                  currency={summary.currency}
                  isRetractation={isRetractationItem(item.reasonId)}
                  isConnectedUser={isAuthenticated}
                  connectedUserTooltip={connectedUserTooltip}
                  onRemove={() => handleRemove(item.lineItemId)}
                />
              ))}
            </ArticleList>
          </div>
        )}
        <Separator />

        {summary.refundTotal > 0 && (
          <ReceiptLine>
            <ReceiptLineLabel>{t("financial.refundTotal")}</ReceiptLineLabel>
            <ReceiptLineValue>
              {formatPrice(summary.refundTotal, summary.currency)}
            </ReceiptLineValue>
          </ReceiptLine>
        )}
        {summary.discountTotal > 0 && (
          <ReceiptLine>
            <ReceiptLineLabel>{t("financial.discount")}</ReceiptLineLabel>
            <ReceiptLineValue>
              {formatPrice(summary.discountTotal, summary.currency)}
            </ReceiptLineValue>
          </ReceiptLine>
        )}
      </div>

      {hasConflict && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            {t("summary.oneResolutionConflict")}
          </AlertDescription>
        </Alert>
      )}

      <AnimatedButtonRow variants={{}}>
        <AnimatedButtonContainer
          layoutId="line-item-back-button"
          disableVariants={{ exit: true }}
        >
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <ArrowLeft />
            {t("common.back")}
          </Button>
        </AnimatedButtonContainer>
        <AnimatedButtonContainer
          layoutId="line-item-confirm-button"
          variants={{}}
        >
          <Button disabled={hasConflict || isValidating} onClick={onConfirm}>
            <EnterIcon />
            {t("common.confirm")}
          </Button>
        </AnimatedButtonContainer>
      </AnimatedButtonRow>

      {remainingReturnableItems.length > 0 && (
        <div className="flex flex-col gap-2 w-full">
          <SectionHeader>{t("common.addAnotherItem")}</SectionHeader>
          <ArticleList className="cursor-pointer">
            {remainingReturnableItems.map((lineItem) => (
              <LineItemRow
                key={lineItem.line_item_id}
                lineItem={lineItem}
                orderId={orderId}
                shopId={shopId}
                currency={summary.currency}
                isConnectedUser={isAuthenticated}
                connectedUserTooltip={connectedUserTooltip}
              />
            ))}
          </ArticleList>
        </div>
      )}
    </WidgetPage>
  );
}
